// Core types — Content Intelligence Factory

export const ROUTE_PATHS = {
  HOME: '/',
  DASHBOARD: '/',
  DISCOVER: '/trends',
  GENERATE: '/pipeline',
  ASSETS: '/assets',
  APPROVAL: '/approval',
  PUBLISH: '/distribution',
  ANALYTICS: '/analytics',
  SETTINGS: '/settings',
  BRAND: '/brand',
  CANVAS: '/canvas',
  GUARDRAILS: '/guardrails',
  HUMANIZER: '/humanizer',
  // legacy aliases
  TRENDS: '/trends',
  PIPELINE: '/pipeline',
  DISTRIBUTION: '/distribution',
} as const;

// Trend Topic Interface
export interface TrendTopic {
  id: string;
  name: string;
  category: string;
  description: string;
  popularity: number; // 0-100
  growthRate: number; // percentage
  keywords: string[];
  sourceUrls: string[];
  discoveredAt: Date;
  lastUpdated: Date;
  status: 'emerging' | 'trending' | 'stable' | 'declining';
  sentiment: 'positive' | 'neutral' | 'negative';
  relevanceScore: number; // 0-100
}

// Content Asset Interface
export interface ContentAsset {
  id: string;
  trendTopicId: string;
  title: string;
  description: string;
  contentType: 'article' | 'video' | 'infographic' | 'social-post' | 'carousel' | 'story';
  format: 'text' | 'image' | 'video' | 'mixed';
  platform: 'instagram' | 'tiktok' | 'youtube' | 'pinterest' | 'twitter' | 'linkedin' | 'blog';
  status: ApprovalStatus;
  createdAt: Date;
  updatedAt: Date;
  publishedAt?: Date;
  scheduledFor?: Date;
  generatedBy: string; // agent ID
  wordCount?: number;
  duration?: number; // in seconds for video
  dimensions?: {
    width: number;
    height: number;
  };
  thumbnailUrl?: string;
  contentUrl?: string;
  tags: string[];
  engagementMetrics?: {
    views: number;
    likes: number;
    shares: number;
    comments: number;
    saves: number;
  };
  performanceScore?: number; // 0-100
}

// Approval Status Type
export type ApprovalStatus = 
  | 'draft'
  | 'pending-review'
  | 'approved'
  | 'rejected'
  | 'published'
  | 'scheduled'
  | 'archived';

// Agent Status Interface
export interface AgentStatus {
  id: string;
  name: string;
  type: 'trend-scout' | 'content-generator' | 'optimizer' | 'scheduler' | 'analyzer';
  status: 'active' | 'idle' | 'busy' | 'error' | 'offline';
  currentTask?: string;
  tasksCompleted: number;
  tasksInQueue: number;
  lastActiveAt: Date;
  uptime: number; // in seconds
  performance: {
    successRate: number; // 0-100
    averageTaskTime: number; // in seconds
    errorCount: number;
  };
  capabilities: string[];
  configuration: Record<string, any>;
}

// Pipeline Week Interface
export interface PipelineWeek {
  id: string;
  weekNumber: number;
  year: number;
  startDate: Date;
  endDate: Date;
  status: 'planning' | 'in-progress' | 'review' | 'completed';
  trends: {
    trendTopicId: string;
    priority: 'high' | 'medium' | 'low';
    targetContentCount: number;
    completedContentCount: number;
  }[];
  contentAssets: string[]; // content asset IDs
  milestones: {
    id: string;
    name: string;
    dueDate: Date;
    completed: boolean;
    completedAt?: Date;
  }[];
  teamMembers: string[];
  notes?: string;
}

// Platform Distribution Interface
export interface PlatformDistribution {
  platform: 'instagram' | 'tiktok' | 'youtube' | 'pinterest' | 'twitter' | 'linkedin' | 'blog';
  contentCount: number;
  scheduledCount: number;
  publishedCount: number;
  engagementRate: number; // 0-100
  reachEstimate: number;
  priority: 'high' | 'medium' | 'low';
  bestPostingTimes: string[]; // ISO time strings
  audienceSize: number;
  growthRate: number; // percentage
}

// Analytics Metrics Interface
export interface AnalyticsMetrics {
  period: 'day' | 'week' | 'month' | 'quarter' | 'year';
  startDate: Date;
  endDate: Date;
  totalContent: number;
  publishedContent: number;
  totalViews: number;
  totalEngagement: number;
  averageEngagementRate: number;
  topPerformingContent: string[]; // content asset IDs
  topTrends: string[]; // trend topic IDs
  platformBreakdown: Record<string, number>;
  contentTypeBreakdown: Record<string, number>;
  audienceGrowth: number;
  revenueGenerated?: number;
}

// User/Team Member Interface
export interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'editor' | 'creator' | 'analyst' | 'viewer';
  avatar?: string;
  department?: string;
  permissions: string[];
  assignedTrends: string[];
  assignedContent: string[];
  lastLogin: Date;
  isActive: boolean;
}

// Notification Interface
export interface Notification {
  id: string;
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
  actionLabel?: string;
  relatedEntityId?: string;
  relatedEntityType?: 'trend' | 'content' | 'agent' | 'pipeline';
}

// Filter and Sort Options
export interface FilterOptions {
  status?: ApprovalStatus[];
  platform?: string[];
  contentType?: string[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  trendTopics?: string[];
  agents?: string[];
  tags?: string[];
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

// Dashboard Summary Interface
export interface DashboardSummary {
  totalTrends: number;
  activeTrends: number;
  totalContent: number;
  pendingApproval: number;
  scheduledContent: number;
  publishedToday: number;
  activeAgents: number;
  totalAgents: number;
  weeklyProgress: number; // 0-100
  engagementRate: number;
  topPerformingPlatform: string;
  recentActivity: {
    timestamp: Date;
    type: string;
    description: string;
    entityId: string;
  }[];
}


// Additional helper types
export type AgentTaskType = 'trend-scout' | 'content-generator' | 'optimizer' | 'scheduler' | 'analyzer';
export type AssetType = 'article' | 'video' | 'infographic' | 'social-post' | 'carousel' | 'story';
